import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Grow from "@mui/material/Grow";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import ButtonBase from "@mui/material/ButtonBase";

/** One row of the picker, as `/v1/public/patient/login` returns it. */
export type PatientChoice = {
  /** Opaque `branch:UHID` pin — sent straight back, never parsed here. */
  id: string;
  name: string;
  age_sex?: string;
  /** Full UHID: the number the clinic asks for, so it's what people match on. */
  uh_id?: string;
  branch_name?: string;
  last_visit_date?: string | null;
  /** Set on the most recently visited record across every branch. */
  is_last_visited?: boolean;
};

type Props = {
  open: boolean;
  patients: PatientChoice[];
  /** id of the row being signed in, so it can show progress. */
  busyId?: string | null;
  onSelect: (patientId: string) => void;
  onCancel: () => void;
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "12 Aug 2026" — always with the year, since a record can go back years. */
function visitDate(iso?: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

function MetaLine({ icon, label, strong }: { icon: React.ReactNode; label: string; strong?: boolean }) {
  return (
    <Stack direction="row" spacing={0.6} alignItems="center" sx={{ mt: 0.4 }}>
      <Box sx={{ display: "flex", color: strong ? "warning.dark" : "text.secondary", "& svg": { fontSize: 16 } }}>
        {icon}
      </Box>
      <Typography
        variant="caption"
        sx={{ color: strong ? "warning.dark" : "text.secondary", fontWeight: strong ? 700 : 400 }}
      >
        {label}
      </Typography>
    </Stack>
  );
}

/**
 * "Who's signing in?" — shown when one contact reaches several patients.
 *
 * A family shares a phone, so a login has to name the person it is for;
 * otherwise a parent's session shows their children's visits and prescriptions
 * pooled into one history. Picking the wrong row scopes the whole portal to a
 * relative, so each card carries what people actually recognise themselves by —
 * full UHID, clinic, and when that record was last seen — and the most recently
 * visited one is led with rather than buried.
 *
 * The dialog cannot be dismissed by clicking away: there is no sensible default
 * patient to fall back to, and Cancel returns to sign-in rather than guessing.
 */
export default function SelectPatientDialog({ open, patients, busyId, onSelect, onCancel }: Props) {
  return (
    <Dialog open={open} onClose={() => {}} maxWidth="xs" fullWidth disableEscapeKeyDown>
      <DialogTitle sx={{ fontWeight: 800 }}>Who&apos;s this for?</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          This contact is linked to {patients.length} patients. Choose who you want to sign in as.
        </DialogContentText>

        <Stack spacing={1.5}>
          {patients.map((p, i) => {
            const highlight = !!p.is_last_visited;
            const busy = busyId === p.id;
            const seen = visitDate(p.last_visit_date);
            return (
              // Cards arrive one after another, which gives the eye an order to
              // read them in rather than presenting a wall of near-identical rows.
              <Grow in timeout={320} style={{ transitionDelay: `${i * 70}ms` }} key={p.id}>
                <Paper
                  variant="outlined"
                  sx={{
                    borderRadius: 3,
                    overflow: "hidden",
                    borderWidth: highlight ? 2 : 1,
                    borderColor: highlight ? "warning.main" : "divider",
                    // Everything dims while one row signs in, so it's obvious
                    // which choice is being acted on.
                    opacity: busyId && !busy ? 0.45 : 1,
                    transition: "opacity .18s, box-shadow .2s, transform .12s",
                    boxShadow: highlight ? 2 : 0,
                    "&:hover": busyId ? {} : { boxShadow: 4, transform: "translateY(-2px)" },
                    "&:active": busyId ? {} : { transform: "scale(0.985)" },
                  }}
                >
                  {/* Said in words as well as colour — the tint alone would be
                      invisible to anyone who can't separate the two. */}
                  {highlight && (
                    <Box
                      sx={{
                        bgcolor: "warning.light",
                        px: 2,
                        py: 0.6,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                      }}
                    >
                      <HistoryRoundedIcon sx={{ fontSize: 16, color: "warning.dark" }} />
                      <Typography
                        variant="caption"
                        sx={{ color: "warning.dark", fontWeight: 800, letterSpacing: 0.9 }}
                      >
                        LAST VISITED
                      </Typography>
                    </Box>
                  )}
                  <ButtonBase
                    // Disabled while any row is in flight, so a second click
                    // can't spend the verification token twice.
                    disabled={!!busyId}
                    onClick={() => onSelect(p.id)}
                    sx={{ width: "100%", p: 2, justifyContent: "flex-start", textAlign: "left" }}
                  >
                    <Avatar
                      sx={{
                        width: 52,
                        height: 52,
                        mr: 1.75,
                        fontWeight: 800,
                        bgcolor: highlight ? "warning.light" : "primary.light",
                        color: highlight ? "warning.dark" : "primary.contrastText",
                      }}
                    >
                      {(p.name || "P").charAt(0).toUpperCase()}
                    </Avatar>

                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap>
                        {p.name || "Patient"}
                      </Typography>
                      {p.age_sex && (
                        <Typography variant="body2" color="text.secondary">
                          {p.age_sex}
                        </Typography>
                      )}
                      {p.uh_id && (
                        <Box
                          sx={{
                            display: "inline-block",
                            mt: 0.75,
                            mb: 0.25,
                            px: 1,
                            py: 0.25,
                            borderRadius: 1,
                            bgcolor: highlight ? "warning.light" : "action.hover",
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 700,
                              letterSpacing: 0.4,
                              color: highlight ? "warning.dark" : "text.secondary",
                            }}
                          >
                            UHID {p.uh_id}
                          </Typography>
                        </Box>
                      )}
                      {p.branch_name && <MetaLine icon={<PlaceOutlinedIcon />} label={p.branch_name} />}
                      <MetaLine
                        icon={<EventAvailableOutlinedIcon />}
                        label={seen ? `Last visit ${seen}` : "No visits yet"}
                        strong={highlight}
                      />
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", pl: 1 }}>
                      {busy ? (
                        <CircularProgress size={22} color={highlight ? "warning" : "primary"} />
                      ) : (
                        <ChevronRightRoundedIcon color={highlight ? "warning" : "primary"} />
                      )}
                    </Box>
                  </ButtonBase>
                </Paper>
              </Grow>
            );
          })}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={!!busyId}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
